import { createContext, ReactNode, useContext, useMemo } from 'react';
import { defaultTheme, type Theme } from './tokens';

const ThemeContext = createContext<Theme | null>(null);

export interface ThemeProviderProps {
  theme?: Partial<Theme> | Theme;
  children: ReactNode;
}

/** Fornece o tema (cores e fontes) para a árvore. Use useTheme() nos componentes. */
export function ThemeProvider({
  theme: themeOverride,
  children,
}: ThemeProviderProps) {
  const value = useMemo<Theme>(() => {
    if (!themeOverride) return defaultTheme;
    return deepMerge(defaultTheme, themeOverride) as Theme;
  }, [themeOverride]);

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): Theme {
  const ctx = useContext(ThemeContext);
  if (!ctx) return defaultTheme;
  return ctx;
}

function deepMerge<T extends object>(target: T, source: Partial<T>): T {
  const out = { ...target };
  for (const key of Object.keys(source) as (keyof T)[]) {
    const a = (out as Record<string, unknown>)[key as string];
    const b = (source as Record<string, unknown>)[key as string];
    if (
      b != null &&
      typeof b === 'object' &&
      !Array.isArray(b) &&
      a != null &&
      typeof a === 'object' &&
      !Array.isArray(a)
    ) {
      (out as Record<string, unknown>)[key as string] = deepMerge(
        a as object,
        b as object
      );
    } else if (b !== undefined) {
      (out as Record<string, unknown>)[key as string] = b;
    }
  }
  return out;
}
